import { useCallback, useEffect, useRef, useState } from 'react'

import * as service from 'service'

const emptyColumnState = () => ({
  tasks: [],
  page: 1,
  totalCount: 0,
  loading: true,
})

const useKanbanColumn = ({
  columns,
  fetchParams,
  refreshKey,
  active,
  onDeleteCard,
}) => {
  const PER_PAGE = 10

  const [columnStates, setColumnStates] = useState({})
  const fetchParamsRef = useRef(fetchParams)
  fetchParamsRef.current = fetchParams
  const lastFetchKeyRef = useRef(null)

  const fetchColumnData = useCallback(async (columnId, page, signal) => {
    try {
      const res = await service.dponet.kanbanTasks.get({
        columnId,
        page,
        perPage: PER_PAGE,
        ...fetchParamsRef.current,
        signal,
      })
      return {
        tasks: res?.data?.tasks || [],
        totalCount: res?.data?.meta?.totalCount || 0,
        error: false,
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED')
        return null
      return { tasks: [], totalCount: 0, error: true }
    }
  }, [])

  const fetchBatch = useCallback(async (batchColumns, signal) => {
    try {
      const columnIds = batchColumns.map((c) => c.id)
      const res = await service.dponet.kanbanTasks.batch({
        columnIds,
        ...fetchParamsRef.current,
        signal,
      })
      return res?.data || []
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED')
        return null
      return []
    }
  }, [])

  const fetchColumnPage = useCallback(
    async (columnId, page, signal) => {
      setColumnStates((prev) => ({
        ...prev,
        [columnId]: {
          ...(prev[columnId] ?? emptyColumnState()),
          loading: true,
        },
      }))

      const result = await fetchColumnData(columnId, page, signal)
      if (!result) return

      setColumnStates((prev) => ({
        ...prev,
        [columnId]: {
          tasks:
            page === 1
              ? result.tasks
              : [...(prev[columnId]?.tasks ?? []), ...result.tasks],
          page,
          totalCount: result.totalCount,
          loading: false,
        },
      }))
    },
    [fetchColumnData],
  )

  const columnIdsKey = columns
    .map((c) => c.id)
    .sort()
    .join(',')

  useEffect(() => {
    if (!active || !columns.length) return
    const fetchKey = `${columnIdsKey}-${JSON.stringify(
      fetchParams,
    )}-${refreshKey}`
    if (lastFetchKeyRef.current === fetchKey) return
    lastFetchKeyRef.current = fetchKey

    const controller = new AbortController()
    setColumnStates((prev) => {
      const preserved = {}
      columns.forEach((col) => {
        preserved[String(col.id)] = {
          ...emptyColumnState(),
          totalCount: prev[String(col.id)]?.totalCount ?? 0,
        }
      })
      return preserved
    })

    const fetchAll = async () => {
      try {
        const result = await fetchBatch(columns, controller.signal)
        if (!result || controller.signal.aborted) return

        setColumnStates((prev) => {
          const updates = {}
          const seen = new Set()
          result.forEach((colData) => {
            if (!colData) return
            const colId = colData.columnId
            const stringId = String(colId)
            seen.add(stringId)
            const tasks = (colData.tasks || []).map((t) =>
              t.targetCompanyId ? { ...t, kanbanColumnId: colId } : t,
            )
            updates[stringId] = {
              tasks,
              page: 1,
              totalCount: colData.meta?.totalCount || 0,
              loading: false,
            }
          })
          // Garante que colunas ausentes no resultado parem de carregar
          columns.forEach((col) => {
            const id = String(col.id)
            if (!seen.has(id)) updates[id] = { ...prev[id], loading: false }
          })
          return { ...prev, ...updates }
        })
      } catch (err) {
        if (controller.signal.aborted) return
        setColumnStates((prev) => {
          const updates = {}
          columns.forEach((col) => {
            const id = String(col.id)
            updates[id] = { ...prev[id], loading: false }
          })
          return { ...prev, ...updates }
        })
      }
    }

    fetchAll()
    return () => controller.abort()
  }, [active, columnIdsKey, fetchParams, refreshKey, fetchBatch]) // eslint-disable-line

  const columnStatesRef = useRef(columnStates)
  columnStatesRef.current = columnStates

  const handleLoadMore = useCallback(
    (columnId) => {
      const state = columnStatesRef.current[columnId]
      if (!state || state.loading) return
      fetchColumnPage(columnId, state.page + 1)
    },
    [fetchColumnPage],
  )

  const loadMoreAllInFlightRef = useRef(false)

  const handleLoadMoreAll = useCallback(async () => {
    if (loadMoreAllInFlightRef.current) return
    const pending = columns.filter((col) => {
      const state = columnStatesRef.current[String(col.id)]
      if (!state || state.loading) return false
      return (state.tasks?.length ?? 0) < (state.totalCount ?? 0)
    })
    if (!pending.length) return

    loadMoreAllInFlightRef.current = true
    try {
      await Promise.all(
        pending.map((col) => {
          const state = columnStatesRef.current[String(col.id)]
          return fetchColumnPage(String(col.id), state.page + 1)
        }),
      )
    } finally {
      loadMoreAllInFlightRef.current = false
    }
  }, [columns, fetchColumnPage])

  const handleDeleteCard = useCallback(
    (task) => {
      const colId = String(task.kanbanColumnId)
      setColumnStates((prev) => {
        const col = prev[colId]
        if (!col) return prev
        return {
          ...prev,
          [colId]: {
            ...col,
            tasks: col.tasks.filter((t) => t.id !== task.id),
            totalCount: Math.max(0, col.totalCount - 1),
          },
        }
      })
      onDeleteCard(task)
    },
    [onDeleteCard],
  )

  return {
    columnStates,
    setColumnStates,
    handleLoadMore,
    handleLoadMoreAll,
    handleDeleteCard,
  }
}

export default useKanbanColumn
